import * as Sentry from "@sentry/react";

import { OrgRecord } from "@/shared/domain/orgs";
import { UserRecord } from "@/shared/domain/users";
import { Env, isProductionLikeStage } from "@/shared/environment";

import { RobotoDomainException } from "../http/exceptions";

import { LoggerService } from "./LoggerService";

class ErrorMonitoringService {
  /**
   * Start Sentry on production-like stages. Call once, from `initErrorMonitoring`
   * only: web workers import this module too, and calling it there would start a
   * second Sentry. Workers forward their failures through `serveCommands`.
   */
  public init() {
    if (isProductionLikeStage()) {
      Sentry.init({
        dsn: Env.sentryDsn,
        environment: Env.appEnv,

        /**
         * These are unactionable network errors where the browser cancels the
         * request and throws a generic error message.
         *
         * This can happen when navigating pages quickly or refreshing the page.
         */
        ignoreErrors: [
          "TypeError: Failed to fetch", // Chrome
          "TypeError: NetworkError when attempting to fetch resource.", // Firefox
          "TypeError: Load failed", // Safari
        ],
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
          // https://docs.sentry.io/platforms/javascript/configuration/integrations/extraerrordata/
          Sentry.extraErrorDataIntegration({
            captureErrorCause: true,
          }),
        ],

        /* Sets the maximum number of levels to which Sentry will serialize extra data */
        normalizeDepth: 10,

        /*************
         *  Tracing  *
         *************/
        // Capture only 10% of the transactions. Sentry's recommendation is to start small
        // then ramp up once we have a stable baseline.
        tracesSampleRate: 0.1,
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", Env.robotoServiceBaseUrl],

        /********************
         *  Session Replay  *
         ********************/
        // We will disable general session replays and only record sessions when errors occur.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  }

  /**
   * Log `error`, then report it to Sentry. A 4XX `RobotoDomainException` is
   * reported only when `options.reportHandledClientError` is set. Before `init`
   * runs, which includes every web worker, this only logs.
   */
  public captureError(
    error: unknown,
    extraData?: Record<string, unknown>,
    options?: { reportHandledClientError?: boolean },
  ) {
    LoggerService.error(error, extraData);

    // A 4XX from our own API is dropped by default: the web UI is assumed to be a correctly
    // implemented client, so a 4XX means the user supplied bad input, which is not a defect worth a
    // Sentry issue. A caller whose request carried no user input sets `reportHandledClientError`,
    // since a 4XX there is a defect: a rollback delete refused with a 403 leaves behind a session the
    // user did not ask for.
    //
    // We deliberately do NOT drop 4XX errors from AWS SDK services we call on the
    // user's behalf (e.g. Secrets Manager, S3), since those point to insufficient
    // form validation or other guardrails on our end.
    if (
      error instanceof RobotoDomainException &&
      error.isClientError() &&
      !options?.reportHandledClientError
    ) {
      return;
    }

    Sentry.captureException(error, {
      extra: extraData,
    });
  }

  public setUser(user: UserRecord) {
    Sentry.setUser({
      id: user.user_id,
      email: user.user_id,
      name: user.name,
    });
  }

  public setOrg(org: OrgRecord) {
    Sentry.setContext("Organization", {
      ...org,
    });
  }
}

const ErrorMonitoringServiceInstance = new ErrorMonitoringService();
export { ErrorMonitoringServiceInstance as ErrorMonitoringService };
